import { useState, ReactNode, Children, isValidElement } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'

interface CollapsibleSectionProps {
  children: ReactNode
  defaultOpen?: boolean
}

export function CollapsibleSection({ children, defaultOpen = false }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  // Extract the heading (first child) and content (rest)
  const childArray = Children.toArray(children)
  let heading: ReactNode = null
  let headingText = ''
  const content: ReactNode[] = []

  childArray.forEach((child, index) => {
    if (index === 0 && isValidElement(child)) {
      // Check if it's an h2 element
      const elementType = child.type
      if (elementType === 'h2' || (typeof elementType === 'string' && elementType === 'h2')) {
        heading = child
        // Extract text content for the summary
        headingText = extractText((child.props as { children?: ReactNode }).children)
      } else {
        content.push(child)
      }
    } else {
      content.push(child)
    }
  })

  // If no h2 heading found, render as regular section
  if (!heading) {
    return <section>{children}</section>
  }

  return (
    <div className="border border-border rounded-lg mb-4 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-4 py-3 bg-muted hover:bg-muted/80 transition-colors text-left"
      >
        {isOpen ? (
          <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
        )}
        <span className="font-semibold text-foreground">{headingText}</span>
      </button>
      {isOpen && (
        <div className="px-4 py-3 prose prose-sm dark:prose-invert max-w-none">
          {content}
        </div>
      )}
    </div>
  )
}

// Helper to extract text from React children
function extractText(children: ReactNode): string {
  if (typeof children === 'string') {
    return children
  }
  if (Array.isArray(children)) {
    return children.map(extractText).join('')
  }
  if (isValidElement(children) && (children.props as { children?: ReactNode }).children) {
    return extractText((children.props as { children?: ReactNode }).children)
  }
  return ''
}
